import { Geometry, Nullable } from '@tcc/common';
import { getSdk, UapiFieldRegionHarvestStatsReport, UapiFieldRegionReportRequestStatus, UapiFieldRegionReportType } from '../../../../../graphql/sdk';
import { getGraphqlClient } from '../../../../../requesters/uapiConnection';
import { SeasonName } from '../../../../../types';
import { ArdFieldRegionReport } from '../../../../../types/FieldRegionReport';
import { Location } from '../../../../../types/Location';
import { adaptMetricStats } from './adapters/statsAdapter';
import {
  mapRootLevelBySeedProductDimension,
  mapRootLevelByElevationDimension,
  mapRootLevelByPopulationDimension,
  mapRootLevelByPlantingDateDimension
} from './adapters/rootLevelAdapters';
import { MAX_POLL_ATTEMPTS, POLL_INTERVAL } from '../../../../../constants/shortPolling';

export enum ArdAsyncStatus {
  Submitted = 'SUBMITTED',
  InProgress = 'IN_PROGRESS',
  Completed = 'COMPLETED',
  Failed = 'FAILED',
}

export interface ArdFetchResponse {
  status: ArdAsyncStatus;
  data: Nullable<ArdFieldRegionReport>;
  error: Nullable<Error>;
  requestId?: string;
  pollAttemptCount?: number;
  submitStartedAt?: number;
  submitCompletedAt?: number;
  pollingStartedAt?: number;
  pollingCompletedAt?: number;
  reportFetchStartedAt?: number;
  reportFetchCompletedAt?: number;
}

export interface ArdFetchRegionReportParams {
  fieldUUID: string;
  cropId: string;
  seasonName: SeasonName;
  seasonYear: number;
  geometry: Geometry | Location | undefined;
  onResponse: (response: ArdFetchResponse) => void;
  reportReferenceId: string;
  requestId?: string;
  pollAttemptCount?: number;
  submitStartedAt?: number;
  submitCompletedAt?: number;
  pollingStartedAt?: number;
  signal?: AbortSignal;
}

const createAbortError = () => {
  const error = new Error('Polling aborted');
  error.name = 'AbortError';
  return error;
};

const throwIfAborted = (signal?: AbortSignal) => {
  if (signal?.aborted) {
    throw createAbortError();
  }
};

const delay = (ms: number, signal?: AbortSignal) => new Promise<void>((resolve, reject) => {
  const timeoutId = setTimeout(() => {
    signal?.removeEventListener('abort', onAbort);
    resolve();
  }, ms);

  const onAbort = () => {
    clearTimeout(timeoutId);
    reject(createAbortError());
  };

  if (signal?.aborted) {
    onAbort();
  } else {
    signal?.addEventListener('abort', onAbort, { once: true });
  }
});

const adaptArdReport = (report: UapiFieldRegionHarvestStatsReport): ArdFieldRegionReport => {
  const stats = report?.stats;
  const dimensions = report?.dimensions;

  return {
    stats: adaptMetricStats(stats),
    dimensions: {
      bySeedProduct: mapRootLevelBySeedProductDimension(
        dimensions?.bySeedProduct ?? []
      ),
      byElevation: mapRootLevelByElevationDimension(
        dimensions?.byElevation ?? []
      ),
      byPopulation: mapRootLevelByPopulationDimension(
        dimensions?.byPopulation ?? []
      ),
      byPlantingDate: mapRootLevelByPlantingDateDimension(
        dimensions?.byPlantingDate ?? []
      ),
    }
  };
};

export async function fetchArdRegionReport({
  onResponse,
  cropId,
  fieldUUID,
  seasonName,
  seasonYear,
  geometry,
  reportReferenceId,
  requestId: cachedRequestId,
  pollAttemptCount: cachedPollAttemptCount,
  submitStartedAt: cachedSubmitStartedAt,
  submitCompletedAt: cachedSubmitCompletedAt,
  pollingStartedAt: cachedPollingStartedAt,
  signal,
}: ArdFetchRegionReportParams): Promise<Nullable<ArdFieldRegionReport>> {
  const sdk = getSdk(getGraphqlClient());
  const now = performance.now();
  const submitStartedAt = cachedSubmitStartedAt ?? now;
  const submitCompletedAt = cachedSubmitCompletedAt ?? now;
  const pollingStartedAt = cachedPollingStartedAt ?? submitCompletedAt;
  const attempts = cachedPollAttemptCount ?? 0;

  throwIfAborted(signal);

  if(geometry === null || geometry === undefined) {
    throw new Error('Invalid Geometry')
  }

  if (cachedRequestId) {
    onResponse({
      status: ArdAsyncStatus.InProgress,
      data: null,
      error: null,
      requestId: cachedRequestId,
      pollAttemptCount: attempts,
      submitStartedAt,
      submitCompletedAt,
      pollingStartedAt,
    });

    return pollRegionReport({
      requestId: cachedRequestId,
      onResponse,
      submitStartedAt,
      submitCompletedAt,
      pollingStartedAt,
      attempts,
      signal,
    });
  }

  const submitResponse = await sdk.submitFieldRegionReportRequest({
    input: {
      cropId,
      fieldId: fieldUUID,
      seasonName,
      seasonYear,
      queries: [
        {
          ref: reportReferenceId,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore geometry types do not match exactly between the SDK and the types,
          // but they are compatible. The geometry from SDK is defined as a custom type
          // referring https://datatracker.ietf.org/doc/html/rfc7946
          region: geometry
        }
      ],
      type: UapiFieldRegionReportType.HARVEST_STATS
    }
  });

  throwIfAborted(signal);

  const mutationResult = submitResponse?.data?.submitFieldRegionReportRequest;
  const submitCompletedAtNewRequest = performance.now();
  const requestId = mutationResult?.result?.id;
  const domainErrors = mutationResult?.errors;

  if (submitResponse.errors && submitResponse.errors.length > 0) {
    const errorMessage = submitResponse.errors[0]?.message || 'Submit request failed';
    const submissionError = new Error(errorMessage);
    onResponse({
      status: ArdAsyncStatus.Failed,
      data: null,
      error: submissionError,
    });

    throw submissionError;
  }

  if (domainErrors && domainErrors.length > 0) {
    const domainErrorMessage = domainErrors[0].reason || 'Domain submit request failed';
    const domainError = new Error(domainErrorMessage);
    onResponse({
      status: ArdAsyncStatus.Failed,
      data: null,
      error: domainError,
    });

    throw domainError;
  }

  if (!requestId) {
    const requestIdError = new Error('Failed to retrieve requestId');
    onResponse({
      status: ArdAsyncStatus.Failed,
      data: null,
      error: requestIdError,
    });

    throw requestIdError;
  }

  onResponse({
    status: ArdAsyncStatus.Submitted,
    data: null,
    error: null,
    requestId,
    pollAttemptCount: 0,
    submitStartedAt,
    submitCompletedAt: submitCompletedAtNewRequest,
  });

  return pollRegionReport({
    requestId,
    onResponse,
    submitStartedAt,
    submitCompletedAt: submitCompletedAtNewRequest,
    pollingStartedAt: submitCompletedAtNewRequest,
    signal,
  });
}

interface PollRegionReportParams {
  requestId: string;
  onResponse: (response: ArdFetchResponse) => void;
  submitStartedAt: number;
  submitCompletedAt: number;
  pollingStartedAt: number;
  attempts?: number;
  signal?: AbortSignal;
}

async function pollRegionReport({
  requestId,
  onResponse,
  submitStartedAt,
  submitCompletedAt,
  pollingStartedAt,
  attempts = 0,
  signal,
}: PollRegionReportParams): Promise<Nullable<ArdFieldRegionReport>> {
  const sdk = getSdk(getGraphqlClient());
  let pollingCompletedAt: number | undefined;
  let reportFetchStartedAt: number | undefined;

  throwIfAborted(signal);
  
  if (attempts >= MAX_POLL_ATTEMPTS) {
    const timeoutError = new Error('Polling timed out');
    onResponse({
      status: ArdAsyncStatus.Failed,
      data: null,
      error: timeoutError,
      requestId,
      pollAttemptCount: attempts,
      submitStartedAt,
      submitCompletedAt,
      pollingStartedAt,
      pollingCompletedAt: performance.now(),
    });

    throw timeoutError;
  }

  try {
    const statusResponse = await sdk.getFieldRegionReportAsyncStatus({ requestId });
    throwIfAborted(signal);
    const request = statusResponse?.data?.fieldRegionReportRequest;
    const status = request?.status;

    if (!status) {
      throw new Error('Field region report status is missing');
    }

    if (status === UapiFieldRegionReportRequestStatus.COMPLETED) {
      pollingCompletedAt = performance.now();
      reportFetchStartedAt = performance.now();
      const reportResponse = await sdk.getFieldRegionReportAsync({ requestId });
      throwIfAborted(signal);
      const reportFetchCompletedAt = performance.now();
      const results = reportResponse?.data?.fieldRegionReportRequest?.results;
      // This cast is necesary becase the SDK have different types for the query result and the report,
      // but they are the same in this case.
      const report = results?.[0]?.report as UapiFieldRegionHarvestStatsReport;

      if (!report) {
         const emptyReportError = new Error('Completed report has no results');
         onResponse({
           status: ArdAsyncStatus.Failed,
           data: null,
           error: emptyReportError,
          requestId,
          pollAttemptCount: attempts + 1,
          submitStartedAt,
          submitCompletedAt,
          pollingStartedAt,
          pollingCompletedAt,
          reportFetchStartedAt,
          reportFetchCompletedAt,
         });
         return null;
      }

      const mappedData = adaptArdReport(report);
      onResponse({
        status: ArdAsyncStatus.Completed,
        data: mappedData,
        error: null,
        requestId,
        pollAttemptCount: attempts + 1,
        submitStartedAt,
        submitCompletedAt,
        pollingStartedAt,
        pollingCompletedAt,
        reportFetchStartedAt,
        reportFetchCompletedAt,
      });
      return mappedData;
    }

    if (status === UapiFieldRegionReportRequestStatus.FAILED) {
      const failedError = new Error('Fields region report request failed');
      onResponse({
        status: ArdAsyncStatus.Failed,
        data: null,
        error: failedError,
        requestId,
        pollAttemptCount: attempts + 1,
        submitStartedAt,
        submitCompletedAt,
        pollingStartedAt,
        pollingCompletedAt: performance.now(),
      });
      return null;
    }

    if (status === UapiFieldRegionReportRequestStatus.PENDING || status === UapiFieldRegionReportRequestStatus.IN_PROGRESS) {
      onResponse({
        status: ArdAsyncStatus.InProgress,
        data: null,
        error: null,
        requestId,
        pollAttemptCount: attempts + 1,
        submitStartedAt,
        submitCompletedAt,
        pollingStartedAt,
      });
    } else {
      throw new Error(`Unsupported field region report status: ${status}`);
    }
  } catch (e: unknown) {
    if ((e as Error).name === 'AbortError') {
      throw e;
    }

    const err = new Error((e as Error)?.message || 'Error occurred during polling');
    onResponse({
      status: ArdAsyncStatus.Failed,
      data: null,
      error: err,
      requestId,
      pollAttemptCount: attempts + 1,
      submitStartedAt,
      submitCompletedAt,
      pollingStartedAt,
      pollingCompletedAt: pollingCompletedAt ?? performance.now(),
      reportFetchStartedAt,
    });
    throw err;
  }

  await delay(POLL_INTERVAL, signal);
  return pollRegionReport({
    requestId,
    onResponse,
    submitStartedAt,
    submitCompletedAt,
    pollingStartedAt,
    attempts: attempts + 1,
    signal,
  });
}